'use client'

import React, { useState } from 'react'
import { useTaskMonitor } from '@/contexts/TaskMonitorContext'
import { TaskItem } from './TaskItem'
import { Button } from '@/shared/ui/button'
import { ListTodo, X, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export function TaskMonitor() {
  const { tasks, removeTask, clearCompletedTasks } = useTaskMonitor()
  const [isOpen, setIsOpen] = useState(false)

  const activeTasks = tasks.filter(t => t.status === 'pending' || t.status === 'processing')
  const completedTasks = tasks.filter(t => t.status === 'success' || t.status === 'error')
  const hasActiveTasks = activeTasks.length > 0

  // 작업이 없으면 아무것도 표시하지 않음
  if (tasks.length === 0) {
    return null
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed bottom-6 right-6 z-50',
          'h-14 w-14 rounded-full shadow-lg',
          'flex items-center justify-center',
          'transition-all duration-200 hover:scale-110',
          hasActiveTasks
            ? 'bg-blue-500 hover:bg-blue-600'
            : 'bg-gray-700 hover:bg-gray-800'
        )}
      >
        <div className="relative">
          <ListTodo className="h-6 w-6 text-white" />
          {hasActiveTasks && (
            <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-xs text-white flex items-center justify-center font-bold">
              {activeTasks.length}
            </span>
          )}
        </div>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide Panel */}
      <div
        className={cn(
          'fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50',
          'transform transition-transform duration-300 ease-in-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">작업 모니터</h2>
            <p className="text-sm text-gray-500">
              {activeTasks.length}개 진행중 · {completedTasks.length}개 완료
            </p>
          </div>
          <div className="flex items-center gap-2">
            {completedTasks.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearCompletedTasks}
                title="완료된 작업 모두 삭제"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <ListTodo className="h-12 w-12 mb-2" />
              <p className="text-sm">진행중인 작업이 없습니다</p>
            </div>
          ) : (
            <>
              {/* Active Tasks */}
              {activeTasks.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    진행중
                  </h3>
                  <div className="space-y-2">
                    {activeTasks.map(task => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onRemove={removeTask}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Tasks */}
              {completedTasks.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    완료
                  </h3>
                  <div className="space-y-2">
                    {completedTasks.map(task => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onRemove={removeTask}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
